import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useLocation, useHistory } from 'react-router-dom'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Box from '@mui/material/Box'
import TextField from '@mui/material/TextField'
import Button from '@mui/material/Button'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import FormControl from '@mui/material/FormControl'
import Modal from '@mui/material/Modal'
import Tooltip from '@mui/material/Tooltip'
import ModeEditIcon from '@mui/icons-material/ModeEdit'
import Title from '../../component/Title'
import API from '../../component/Axios'
import Snackbars from '../../component/Snackbars'
import Spiner from '../../component/Spiner'
import ListActionGroup from '../../component/ListActionGroup'
import ListEmptyState from '../../component/ListEmptyState'
import BottomButtonGroup from '../../component/BottomButtonGroup'
import { LIST_ACTION_EDIT_BUTTON_SX } from '../../component/listActionIconStyles'
import { buildListSearch as buildListSearchQuery, createInitialListState, getLocationState } from '../../utils/listPageState'

const style2 = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: { xs: 'calc(100vw - 32px)', sm: 600 },
    maxWidth: '100%',
    bgcolor: 'background.paper',
    border: '1px solid rgba(148, 163, 184, 0.32)',
    borderRadius: '8px',
    boxShadow: '0 24px 64px rgba(15, 23, 42, 0.22)',
    outline: 'none',
    p: { xs: 2, sm: 3 },
}

const DEFAULT_MODAL_VALUES = {
    deviceType: '',
    number1: 0,
    number2: 0
}

const createVersionModalValues = (overrides = {}) => ({
    ...DEFAULT_MODAL_VALUES,
    ...overrides,
})

export default function AppVersions() {
    const location = useLocation()
    const history = useHistory()
    const initialListStateRef = useRef(createInitialListState({
        location,
        getExtraState: ({ searchParams }) => ({
            modalDeviceType: searchParams.get('device') || '',
            draftNumber1: searchParams.has('draftNumber1') ? (searchParams.get('draftNumber1') || '') : null,
            draftNumber2: searchParams.has('draftNumber2') ? (searchParams.get('draftNumber2') || '') : null,
        }),
    }))
    const initialListState = initialListStateRef.current
    const modalHydratedRef = useRef(false)
    const locationState = getLocationState(location)
    const [loading, setLoading] = useState(true)
    const [saving, setSaving] = useState(false)
    const [params, setParams] = useState([])
    const [openSnackbar, setOpenSnackbar] = useState(locationState.openSnackbar || false)
    const [severitySnackbar, setSeveritySnackbar] = useState(locationState.severitySnackbar || 'info')
    const [messageSnackbar, setMessageSnackbar] = useState(locationState.messageSnackbar || '')
    const [listErrorMessage, setListErrorMessage] = useState('')

    const getData = useCallback(async (showSpinner = true) => {
        if (showSpinner) {
            setLoading(true)
        }
        setListErrorMessage('')

        try {
            const { data } = await API.post('/api/v1/admin/appVersion/getVersion', { })
            if (data.code === 200) {
                setParams(Array.isArray(data.data) ? data.data : [])
                setListErrorMessage('')
            } else if (data.code === 403) {
                history.push('/login')
                return
            } else {
                const errorMessage = data.message || 'App 버전 정보를 불러오지 못했습니다.'
                setParams([])
                setListErrorMessage(errorMessage)
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(errorMessage)
            }
        } catch (_error) {
            const errorMessage = 'App 버전 정보를 불러오지 못했습니다.'
            setParams([])
            setListErrorMessage(errorMessage)
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar(errorMessage)
        } finally {
            setLoading(false)
        }
    }, [history])

    useEffect(() => {
        getData()
    }, [getData])

    const handleClose = () => {
        setOpenSnackbar(false)
    }

    const [openModal, setOpenModal] = useState(Boolean(initialListState.modalDeviceType))
    const [values, setValues] = useState(createVersionModalValues({
        deviceType: initialListState.modalDeviceType || '',
        number1: initialListState.draftNumber1 ?? DEFAULT_MODAL_VALUES.number1,
        number2: initialListState.draftNumber2 ?? DEFAULT_MODAL_VALUES.number2,
    }))

    const syncModalValues = useCallback((row) => {
        const tempNum = String(row?.number || '').split('.')

        setValues(createVersionModalValues({
            deviceType: row?.deviceType || '',
            number1: tempNum[0] || 0,
            number2: tempNum[1] || 0
        }))
    }, [])

    useEffect(() => {
        const nextSearch = buildListSearchQuery({
            curPage: 1,
            searchQuery: '',
            extraParams: [
                {
                    param: 'device',
                    value: values.deviceType,
                    shouldInclude: (value) => openModal && Boolean(value),
                },
                {
                    param: 'draftNumber1',
                    value: values.number1,
                    shouldInclude: () => openModal,
                },
                {
                    param: 'draftNumber2',
                    value: values.number2,
                    shouldInclude: () => openModal,
                },
            ],
        })

        if (location.search !== nextSearch) {
            history.replace({
                pathname: location.pathname,
                search: nextSearch,
            })
        }
    }, [history, location.pathname, location.search, openModal, values.deviceType, values.number1, values.number2])

    // 새로고침으로 복귀했을 때 현재 목록 데이터에서 다시 찾아 같은 디바이스 수정 모달과 draft 값을 복원한다.
    useEffect(() => {
        if (loading || modalHydratedRef.current || !initialListState.modalDeviceType) {
            return
        }

        const currentRow = params.find((row) => String(row.deviceType) === String(initialListState.modalDeviceType))
        modalHydratedRef.current = true

        if (!currentRow) {
            setValues(createVersionModalValues())
            setOpenModal(false)
            return
        }

        const tempNum = String(currentRow?.number || '').split('.')
        setValues(createVersionModalValues({
            deviceType: currentRow?.deviceType || '',
            number1: initialListState.draftNumber1 ?? (tempNum[0] || 0),
            number2: initialListState.draftNumber2 ?? (tempNum[1] || 0),
        }))
        setOpenModal(true)
    }, [initialListState.draftNumber1, initialListState.draftNumber2, initialListState.modalDeviceType, loading, params])

    const handleCloseModal = () => {
        if (saving) {
            return
        }

        setOpenModal(false)
        setValues(createVersionModalValues())
    }

    const handleUpdateField = (e) => {
        setValues({
            ...values,
            [e.target.name]: e.target.value
        })
    }

    const handleModifyClick = (row) => {
        syncModalValues(row)
        setOpenModal(true)
    }

    const resetModalState = () => {
        setValues(createVersionModalValues())
        setOpenModal(false)
    }

    const updateVersion = async () => {
        if (saving) {
            return
        }

        const nextDeviceType = String(values.deviceType || '').trim()
        const nextNumber1 = String(values.number1 ?? '').trim()
        const nextNumber2 = String(values.number2 ?? '').trim()

        if (!nextDeviceType || nextNumber1 === '' || nextNumber2 === '') {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('디바이스와 버전 정보를 모두 입력하세요.')
            return
        }

        if (!/^\d+$/.test(nextNumber1) || !/^\d+$/.test(nextNumber2)) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('버전은 0 이상의 정수만 입력하세요.')
            return
        }

        try {
            setSaving(true)

            const { data } = await API.post('/api/v1/admin/appVersion/update', {
                deviceType: nextDeviceType,
                number: `${nextNumber1}.${nextNumber2}`
            })

            if (data.code === 200) {
                setOpenSnackbar(true)
                setSeveritySnackbar('success')
                setMessageSnackbar(data.message)
                resetModalState()
                getData()
            } else if (data.code === 403) {
                history.push('/login')
            } else {
                setOpenSnackbar(true)
                setSeveritySnackbar('error')
                setMessageSnackbar(data.message)
            }
        } catch (_error) {
            setOpenSnackbar(true)
            setSeveritySnackbar('error')
            setMessageSnackbar('App 버전을 저장하지 못했습니다.')
        } finally {
            setSaving(false)
        }
    }

    const renderEmptyState = () => {
        if (listErrorMessage) {
            return (
                <ListEmptyState
                    variant="error"
                    title="App 버전 정보를 불러오지 못했습니다."
                    description={listErrorMessage}
                    action={(
                        <Button
                            type="button"
                            size="small"
                            variant="outlined"
                            onClick={() => getData(false)}
                            disabled={saving}
                        >
                            다시 조회
                        </Button>
                    )}
                />
            )
        }

        return (
            <ListEmptyState
                title="등록된 App 버전 정보가 없습니다."
                description="App 버전 정보가 등록되면 이 목록에 표시됩니다."
            />
        )
    }

    return (
        <Grid item xs={12}>
            {loading ? (
                <Spiner/>
            ) : (
                <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column' }}>
                    {/* 타이틀 */}
                    <Title>App 버전 정보</Title>
                    
                    <Box sx={{ opacity: saving ? 0.5 : 1, pointerEvents: saving ? 'none' : 'auto' }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>디바이스</TableCell>
                                    <TableCell>버전</TableCell>
                                    {/* <TableCell>시즌타이틀</TableCell> */}
                                    <TableCell align="right">Action</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {params.length > 0 ? (
                                    params.map((row) => (
                                        <TableRow key={row.deviceType}>
                                            <TableCell>{row.deviceType}</TableCell>
                                            <TableCell>{row.number}</TableCell>
                                            {/* <TableCell>{row.seasonText}</TableCell> */}
                                            <TableCell align="right">
                                                <ListActionGroup>
                                                    <Tooltip title="Edit">
                                                        <Box
                                                            component="button"
                                                            type="button"
                                                            onClick={() => handleModifyClick(row)}
                                                            aria-label="Edit"
                                                            disabled={saving}
                                                            sx={{
                                                                ...LIST_ACTION_EDIT_BUTTON_SX,
                                                                cursor: saving ? 'default' : 'pointer',
                                                                opacity: saving ? 0.5 : 1,
                                                                color: saving ? 'rgba(100, 116, 139, 0.42)' : LIST_ACTION_EDIT_BUTTON_SX.color,
                                                            }}
                                                        >
                                                            <ModeEditIcon />
                                                        </Box>
                                                    </Tooltip>
                                                </ListActionGroup>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={3}>
                                            {renderEmptyState()}
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </Box>
                    
                    <Modal open={openModal} onClose={handleCloseModal} aria-label="App 버전 수정">
                        <Box
                            component="form"
                            sx={style2}
                            onSubmit={(event) => {
                                event.preventDefault()
                                updateVersion()
                            }}
                        >
                            <Grid container spacing={2} sx={{ marginTop: 2 }}>
                                <Grid item xs={12} sm={12} md={12}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="디바이스"
                                            id="deviceType"
                                            name="deviceType"
                                            value={values.deviceType || ''}
                                            size="small"
                                            InputProps={{ readOnly: true }}
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2} sm={2} md={2}>
                                    <FormControl fullWidth sx={{ marginRight:1 }} >
                                        <TextField
                                            label="버전"
                                            required
                                            id="number1"
                                            name="number1"
                                            type="number"
                                            disabled={saving}
                                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                                            value={values.number1 ?? ''}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                <Grid item xs={2} sm={2} md={2}>
                                    <FormControl fullWidth sx={{ marginRight:1 }} >
                                        <TextField
                                            label="버전"
                                            required
                                            id="number2"
                                            name="number2"
                                            type="number"
                                            disabled={saving}
                                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                                            value={values.number2 ?? ''}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid>
                                {/* <Grid item xs={12} sm={12} md={12}>
                                    <FormControl fullWidth>
                                        <TextField
                                            label="시즌타이틀"
                                            id="seasonText"
                                            name="seasonText"
                                            value={values.seasonText || ''}
                                            onChange={handleUpdateField}
                                            size="small"
                                        />
                                    </FormControl>
                                </Grid> */}
                                <Grid item xs={12} sm={12} md={12}>
                                    <BottomButtonGroup
                                        variant="formAction"
                                        visible
                                        backLabel="닫기"
                                        submitDisabled={saving}
                                        submitLabel={saving ? '저장 중...' : '저장'}
                                        onBackClick={handleCloseModal}
                                        actionTitle="버전 저장"
                                        actionDescription="입력한 앱 버전 정보를 저장합니다."
                                    />
                                </Grid>
                            </Grid>
                        </Box>
                    </Modal>
                    {openSnackbar && <Snackbars open={openSnackbar} severity={severitySnackbar} message={messageSnackbar} handleClose={handleClose}/>}
                </Paper>
            )}
        </Grid>
    )
}
